import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, User, Mail, Lock, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PerfilAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const data = localStorage.getItem('admin_data');
    
    if (!token || !data) {
      navigate('/login');
      return;
    }
    
    const admin = JSON.parse(data);
    setAdminData(admin);
    setFormData(prev => ({
      ...prev,
      nome: admin.admin_name,
      email: admin.admin_email
    }));
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return;
    }

    // Validações
    if (formData.new_password) {
      if (!formData.current_password) {
        toast.error('Senha atual é obrigatória para alterar senha');
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        toast.error('As senhas não coincidem');
        return;
      }
      if (formData.new_password.length < 6) {
        toast.error('A nova senha deve ter pelo menos 6 caracteres');
        return;
      }
    }

    setLoading(true);
    try {
      const updateData = {
        nome: formData.nome,
        email: formData.email
      };

      if (formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      const res = await axios.put(`${API}/admin/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Atualizar localStorage
      const updatedData = {
        admin_id: adminData.admin_id,
        admin_name: res.data.admin.nome,
        admin_email: res.data.admin.email
      };
      localStorage.setItem('admin_data', JSON.stringify(updatedData));
      
      toast.success('Perfil atualizado com sucesso!');
      
      // Limpar campos de senha
      setFormData(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  if (!adminData) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <p className="font-inter text-gray-500">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <User className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">Meu Perfil</h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#E1ECE5]">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="pb-6 border-b border-gray-200">
              <h2 className="font-nunito font-bold text-xl text-[#1A4D2E]">
                Informações Pessoais
              </h2>
              <p className="font-inter text-sm text-gray-500 mt-1">
                Atualize seus dados de acesso
              </p>
            </div>

            {/* Nome */}
            <div>
              <Label htmlFor="nome" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Nome
              </Label>
              <Input
                id="nome"
                name="nome"
                data-testid="input-nome"
                value={formData.nome}
                onChange={handleChange}
                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50"
                required
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="input-email"
                value={formData.email}
                onChange={handleChange}
                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50"
                required
              />
            </div>

            <div className="py-6 border-t border-gray-200">
              <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-2">
                Alterar Senha
              </h3>
              <p className="font-inter text-sm text-gray-500 mb-4">
                Deixe em branco se não quiser alterar
              </p>
            </div>

            {/* Senha Atual */}
            <div>
              <Label htmlFor="current_password" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Senha Atual
              </Label>
              <div className="relative">
                <Input
                  id="current_password"
                  name="current_password"
                  type={showCurrent ? 'text' : 'password'}
                  data-testid="input-current-password"
                  value={formData.current_password}
                  onChange={handleChange}
                  className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50 pr-10"
                  placeholder="Digite sua senha atual"
                />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-current-password">
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Nova Senha */}
            <div>
              <Label htmlFor="new_password" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Nova Senha
              </Label>
              <div className="relative">
                <Input
                  id="new_password"
                  name="new_password"
                  type={showNew ? 'text' : 'password'}
                  data-testid="input-new-password"
                  value={formData.new_password}
                  onChange={handleChange}
                  className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50 pr-10"
                  placeholder="Mínimo 6 caracteres"
                />
                <button type="button" onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-new-password">
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirmar Senha */}
            <div>
              <Label htmlFor="confirm_password" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Confirmar Nova Senha
              </Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type={showConfirm ? 'text' : 'password'}
                  data-testid="input-confirm-password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50 pr-10"
                  placeholder="Repita a nova senha"
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-confirm-password">
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold shadow-md active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </form>
        </Card>
      </div>

      <Navigation active="perfil" />
    </div>
  );
};

export default PerfilAdmin;
